import { AnimatePresence, motion } from "framer-motion";
import { useState, useEffect } from "react";
import { type MarkdownPost } from "../utils/data";
import { useData } from "../context/DataContext";
import ProjectCard from "../components/ProjectCard";
import Breadcrumb from "../components/Breadcrumb";

export default function Projects() {
    // Global Data
    const { data: globalData } = useData();
    const header = globalData.projects.header;
    const projects = globalData.projects.list;

    // Effective Loading State: Show skeleton if NO data OR if transitioning
    const [isTransitioning, setIsTransitioning] = useState(true);

    // Frame Budget: Wait for page transition to finish before rendering heavy grid
    useEffect(() => {
        const timer = setTimeout(() => setIsTransitioning(false), 350);
        return () => clearTimeout(timer);
    }, []);

    // Combine loading states
    const isLoading = !header || isTransitioning;

    // Expandable Filter State
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    // Search & Filter State
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedTag, setSelectedTag] = useState<string | null>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    // Default fallback while loading
    const displayHeader = header || {
        header: { badge: 'Projects', title: 'Haberleşme', description: 'Yükleniyor...' }
    };


    const radiusClass = "rounded-[20px]";
    const borderClass = "border-2 border-[#2A2A2A] hover:border-[#404040]";

    const containerVariants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.04,
                delayChildren: 0
            }
        }
    };

    const itemVariants: any = {
        hidden: { opacity: 0, y: 20, scale: 0.98 },
        show: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.4,
                ease: "backOut"
            }
        }
    };

    // Compute unique tags
    const allTags = Array.from(new Set(projects.flatMap(p => p.frontmatter.tags || []))).sort();

    // Filter Logic
    const filteredProjects = projects.filter(project => {
        const matchesSearch = (project.frontmatter.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            project.frontmatter.desc?.toLowerCase().includes(searchQuery.toLowerCase()));
        const matchesTag = selectedTag ? project.frontmatter.tags?.includes(selectedTag) : true;

        return matchesSearch && matchesTag;
    });

    return (
        <motion.div
            initial="hidden"
            animate="show"
            exit={{ opacity: 0 }}
            variants={containerVariants}
            className="min-h-screen flex flex-col items-center py-24 px-4 md:py-16 pb-32"
        >
            <div className="w-full max-w-4xl mx-auto h-auto">

                {/* 1. CONTROL ROW (Breadcrumb + Search/Filter) */}
                <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 mb-8">

                    {/* LEFT: Breadcrumb Box */}
                    <div className={`bg-[var(--card-bg)] ${radiusClass} ${borderClass} px-8 flex items-center h-[54px]`}>
                        <Breadcrumb items={[{ label: "Projects" }]} />
                    </div>

                    {/* RIGHT: Search & Filter Box */}
                    <div className="flex items-center gap-3 relative z-30">

                        {/* Search Input */}
                        <div className="relative h-[54px] w-full md:w-64">
                            <input
                                type="text"
                                placeholder="Projelerde ara..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className={`w-full h-full bg-[var(--card-bg)] ${radiusClass} ${borderClass} text-white text-sm px-4 pr-10 placeholder-gray-500 focus:outline-none focus:border-[var(--accent)] transition-colors`}
                            />
                            <div className="absolute right-0 top-0 h-full w-10 flex items-center justify-center text-gray-500 pointer-events-none">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                        </div>

                        {/* Dropdown */}
                        <div className="relative h-[54px] min-w-[160px]">
                            <button
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                className={`w-full h-full bg-[var(--card-bg)] ${radiusClass} ${borderClass} text-xs font-bold px-4 flex items-center justify-between transition-colors ${isDropdownOpen ? 'border-[var(--accent)] text-white' : 'border-[#2A2A2A] text-gray-300 hover:border-[#444]'
                                    }`}
                            >
                                <span className="truncate">{selectedTag || "Kategori Seçin"}</span>
                                <svg className={`w-3.5 h-3.5 text-gray-500 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            <AnimatePresence>
                                {isDropdownOpen && (
                                    <>
                                        <div className="fixed inset-0 z-40" onClick={() => setIsDropdownOpen(false)} />
                                        <motion.div
                                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                            transition={{ duration: 0.2 }}
                                            className="absolute top-full right-0 mt-2 w-full bg-[#151515] border-2 border-[#2A2A2A] rounded-xl shadow-2xl overflow-hidden z-50 p-1.5"
                                        >
                                            <button
                                                onClick={() => { setSelectedTag(null); setIsDropdownOpen(false); }}
                                                className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-all border-2 mb-1 ${selectedTag === null ? 'bg-[var(--accent)]/10 border-[var(--accent)] text-[var(--accent)]' : 'border-transparent text-gray-400 hover:bg-[#222] hover:text-white hover:border-[#333]'}`}
                                            >
                                                Tümü
                                            </button>
                                            {allTags.map(tag => (
                                                <button
                                                    key={tag}
                                                    onClick={() => { setSelectedTag(tag); setIsDropdownOpen(false); }}
                                                    className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-all border-2 mb-1 ${selectedTag === tag ? 'bg-[var(--accent)]/10 border-[var(--accent)] text-[var(--accent)]' : 'border-transparent text-gray-400 hover:bg-[#222] hover:text-white hover:border-[#333]'}`}
                                                >
                                                    {tag}
                                                </button>
                                            ))}
                                        </motion.div>
                                    </>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>
                </motion.div>

                {/* 2. PROJECTS GRID */}
                <motion.div
                    className="grid grid-cols-1 md:grid-cols-2 gap-4 auto-rows-fr [&>*:nth-child(odd):last-child]:md:col-span-2"
                    variants={containerVariants}
                >
                    <AnimatePresence mode="wait">
                        {isLoading ? (
                            <>
                                {[1, 2, 3, 4].map((i) => (
                                    <motion.div
                                        key={`skeleton-${i}`}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        className="bg-[#111] rounded-[24px] border border-[#222] p-8 flex flex-col justify-between h-[340px] animate-pulse relative overflow-hidden"
                                    >
                                        <div className="flex justify-between items-start">
                                            <div className="w-14 h-14 bg-[#222] rounded-2xl opacity-40" />
                                            <div className="w-24 h-7 bg-[#222] rounded-full opacity-20" />
                                        </div>
                                        <div className="space-y-4">
                                            <div className="w-4/5 h-9 bg-[#222] rounded-lg opacity-50" />
                                            <div className="space-y-2">
                                                <div className="w-full h-4 bg-[#222] rounded opacity-20" />
                                                <div className="w-3/4 h-4 bg-[#222] rounded opacity-20" />
                                            </div>
                                            <div className="flex gap-2 pt-2">
                                                <div className="w-20 h-7 bg-[#222] rounded opacity-30" />
                                                <div className="w-16 h-7 bg-[#222] rounded opacity-30" />
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </>
                        ) : (
                            filteredProjects.map((project, i) => (
                                <motion.div
                                    key={project.slug}
                                    variants={itemVariants}
                                    exit={{ opacity: 0, y: 10 }}
                                    style={{ willChange: "transform, opacity" }}
                                >
                                    <ProjectCard project={project} index={i} />
                                </motion.div>
                            ))
                        )}
                    </AnimatePresence>
                </motion.div>

                {!isLoading && filteredProjects.length === 0 && (
                    <motion.div variants={itemVariants} className="py-20 text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#1a1a1a] text-gray-600 mb-4">
                            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h3 className="text-white font-bold text-lg mb-1">Sonuç Bulunamadı</h3>
                        <p className="text-gray-500 text-sm">Arama kriterlerinize uygun proje yok.</p>
                        <button
                            onClick={() => { setSearchQuery(""); setSelectedTag(null); }}
                            className="mt-6 text-[var(--accent)] text-xs font-bold uppercase tracking-wider hover:underline"
                        >
                            Filtreleri Temizle
                        </button>
                    </motion.div>
                )}
            </div>
        </motion.div >
    );
}
